import classNames from 'classnames';
import Link from 'next/link';
import { FC } from 'react';
import { SWRResponse } from 'swr';
import { shallow } from 'zustand/shallow';

import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { IconClose } from 'src/general/Icons/IconClose';
import { carComparisonStore } from 'src/stores/carComparisonStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { ButtonIcon } from '../../../general/components/Button/ButtonIcon';
import { Image } from '../../../general/components/Image/Image';
import { Shade } from '../../../general/components/Shade/Shade';
import { Skeleton } from '../../../general/components/Skeleton/Skeleton';
import styles from './CarComparisonBannerTile.module.scss';

type Props = {
  sku?: string;
  vehicle?: SWRResponse<ProductDetailsPayload>;
};

export const CarComparisonBannerTile: FC<Props> = ({ sku, vehicle }) => {
  if (vehicle?.error) {
    return (
      <div className={classNames(styles.root, styles.rootMessage, styles.rootMessageError)}>
        <p className="c-fs-body2">Error loading vehicle data</p>
        <RemoveButton sku={sku} />
      </div>
    );
  }

  if (vehicle?.isLoading) {
    return (
      <div className={classNames(styles.root, styles.rootTile)}>
        <div className={styles.carImage} />
        <p className={classNames(styles.carName, 'c-fs-caption2', 'c-fw-bold')}>
          <Skeleton size={80} />
        </p>
        <p className={classNames(styles.carKms, 'c-fs-caption2')}>
          <Skeleton size={30} />
        </p>
        <p className={classNames(styles.carPrice, 'c-fs-caption', 'c-fw-bold')}>
          <Skeleton size={40} />
        </p>
        <RemoveButton sku={sku} />
      </div>
    );
  }

  if (vehicle?.data) {
    const carData = productDetailPayloadToBaseCarData(vehicle.data);

    return (
      <div className={classNames(styles.root, styles.rootTile)}>
        <Image className={styles.carImage} src={carData.imageUrl} width={120} height={66} alt={carData.model} />
        <p className={classNames(styles.carName, 'c-fs-caption2', 'c-fw-bold')}>{carData.model}</p>
        <p className={classNames(styles.carKms, 'c-fs-caption2')}>
          {formatNumber(carData.kilometers, { append: ' km' })}
        </p>
        <p className={classNames(styles.carPrice, 'c-fs-caption', 'c-fw-bold')}>
          {formatNumber(carData.price, { prepend: '$' })}
        </p>
        <Link
          href={`/product-detail/${sku}`}
          className={classNames(styles.link, 'shade_container', 'shade_container--grey')}
          data-testid="CarComparisonBannerTile-Link"
        >
          <Shade />
          <span className="sr-only">{carData.model}</span>
        </Link>
        <RemoveButton sku={sku} />
      </div>
    );
  }

  return (
    <div className={classNames(styles.root, styles.rootMessage)}>
      <p className="c-fs-body2">Add another car to compare</p>
    </div>
  );
};

const RemoveButton: FC<{ sku?: string }> = ({ sku }) => {
  const { removeSku } = carComparisonStore((state) => ({ removeSku: state.removeSku }), shallow);

  const onRemoveSku = () => {
    if (sku) {
      pushToDataLayer({
        event: `car_comparison-remove_sku`,
        sku,
      });
      removeSku(sku);
    }
  };

  return (
    <div className={styles.removeButton}>
      <ButtonIcon
        onClick={onRemoveSku}
        Icon={IconClose}
        title="Remove vehicle"
        size="xsmall"
        variant="secondary"
        styling="round"
        data-testid="CompareCar-RemoveButton"
      />
    </div>
  );
};
